import { useId, type ComponentType } from "react";

// Sidebar-sized national flags (16px box, 16x11 flag, rounded corners). Drawn
// inline because emoji flags render as plain letters on Windows. Colours are
// the flags' own, not theme tokens.
export type FlagProps = { size?: number; className?: string; strokeWidth?: number };
export type FlagIcon = ComponentType<FlagProps>;

function FlagFrame({ size = 16, className, children }: FlagProps & { children: React.ReactNode }) {
  const clip = useId();
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" className={className} aria-hidden>
      <clipPath id={clip}>
        <rect x="0" y="2.5" width="16" height="11" rx="1.5" />
      </clipPath>
      <g clipPath={`url(#${clip})`}>{children}</g>
    </svg>
  );
}

export function VietnamFlag(props: FlagProps) {
  return (
    <FlagFrame {...props}>
      <rect x="0" y="2.5" width="16" height="11" fill="#DA251D" />
      <polygon
        fill="#FFFF00"
        points="8.00,4.70 8.79,6.91 11.14,6.98 9.28,8.42 9.94,10.67 8.00,9.35 6.06,10.67 6.72,8.42 4.86,6.98 7.21,6.91"
      />
    </FlagFrame>
  );
}

// Simplified: 7 stripes and the blue canton, no stars (unreadable at 16px).
export function UsFlag(props: FlagProps) {
  const stripe = 11 / 7;
  return (
    <FlagFrame {...props}>
      <rect x="0" y="2.5" width="16" height="11" fill="#FFFFFF" />
      {[0, 2, 4, 6].map((i) => (
        <rect key={i} x="0" y={2.5 + i * stripe} width="16" height={stripe} fill="#B22234" />
      ))}
      <rect x="0" y="2.5" width="7" height={stripe * 4} fill="#3C3B6E" />
    </FlagFrame>
  );
}

const FLAG_BY_CURRENCY: Record<string, FlagIcon> = { VND: VietnamFlag, USD: UsFlag };

// Keyed on currency, not market name, so it holds for any org's nodes.
export function flagForCurrency(currency: string | null | undefined): FlagIcon | undefined {
  return currency ? FLAG_BY_CURRENCY[currency] : undefined;
}
